<template>
  <CardProgress
    :state="state"
    :progress="progressPercent"
    :message="statusMessage"
    :show-value="showLabel && state !== CardProgressState.COMPLETED"
  >
    <template #icon>
      <span class="flex shrink-0 items-center">
        <Icon :name="statusIconName" class="size-[0.875rem]" />
      </span>
    </template>
  </CardProgress>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from '#imports';
import {
  CardProgress,
  CardProgressState,
  Icon,
  IconName,
} from '@exfluential/ui';
import { UserProgressStatus } from '~/api/learning';
import { getCardProgressState } from '~/components/learning/progress-status';

type Props = {
  progressPercent: number;
  status?: UserProgressStatus;
  showLabel?: boolean;
  completedActivities?: number;
  totalActivities?: number;
  statusMessageOn?: boolean;
  canRetake?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  status: UserProgressStatus.IN_PROGRESS,
  showLabel: false,
  completedActivities: 0,
  totalActivities: 0,
});

const { t } = useI18n();

const state = computed(() => getCardProgressState(props.status));

const statusIconName = computed(() => {
  switch (props.status) {
    case UserProgressStatus.COMPLETED:
    case UserProgressStatus.PASSED: {
      return IconName.CUP;
    }
    case UserProgressStatus.IN_PROGRESS: {
      return IconName.LIKE;
    }
    case UserProgressStatus.AWAITING_REVIEW: {
      return IconName.CLOCK;
    }
    case UserProgressStatus.NOT_PASSED: {
      return IconName.WARNING;
    }
    default: {
      return IconName.STAR_LEARN;
    }
  }
});

const statusMessage = computed(() => {
  if (!props.statusMessageOn) {
    return '';
  }

  switch (props.status) {
    case UserProgressStatus.NOT_STARTED: {
      return t('learning.progressMessage.notStarted');
    }
    case UserProgressStatus.IN_PROGRESS: {
      return t('learning.progressMessage.inProgress');
    }
    case UserProgressStatus.AWAITING_REVIEW: {
      return t('learning.progressMessage.inReview');
    }
    case UserProgressStatus.COMPLETED: {
      return t('learning.progressMessage.completed');
    }
    case UserProgressStatus.PASSED: {
      return t('learning.progressMessage.passed');
    }
    case UserProgressStatus.NOT_PASSED: {
      return props.canRetake
        ? t('learning.progressMessage.failed-with-retake')
        : t('learning.progressMessage.failed');
    }
    default: {
      return '';
    }
  }
});
</script>
